import {
  groupTrackNumbers,
  groupTracks,
  insertTrack,
  moveTracks,
  removeTrack,
  renameTrack,
  setTrackHidden,
  trackCount,
  trackInfo,
  ungroupTracks,
  updateTrackGroup,
} from "@cva/shared";
import type { EditorStore } from "../store";

/**
 * Track commands of the video timeline (packages/shared/src/video/tracks.ts):
 * every change goes through the document store (undo, live editing).
 */

/** A new empty track at `at` (default: on top). Returns its number. */
export function addTrack(store: EditorStore, at?: number): number {
  let created = 0;
  store.getState().update((draft) => {
    created = insertTrack(draft, at ?? trackCount(draft) + 1);
  });
  return created;
}

export function deleteTrack(store: EditorStore, track: number) {
  store.getState().update((draft) => removeTrack(draft, track));
}

export function setTrackName(store: EditorStore, track: number, name: string) {
  store.getState().update((draft) => renameTrack(draft, track, name));
}

export function toggleTrackHidden(store: EditorStore, track: number) {
  const hidden = Boolean(trackInfo(store.getState().doc, track)?.hidden);
  store.getState().update((draft) => setTrackHidden(draft, track, !hidden));
}

/** Moves tracks `first … first + count - 1` right below the track now at `before`. */
export function moveTrackRows(store: EditorStore, first: number, count: number, before: number) {
  store.getState().update((draft) => {
    moveTracks(draft, first, count, before);
  });
}

export function groupTrackRows(store: EditorStore, tracks: readonly number[]): string | null {
  let group: string | null = null;
  store.getState().update((draft) => {
    group = groupTracks(draft, tracks);
  });
  return group;
}

export function ungroupTrackRows(store: EditorStore, groupId: string) {
  store.getState().update((draft) => ungroupTracks(draft, groupId));
}

export function setGroupCollapsed(store: EditorStore, groupId: string, collapsed: boolean) {
  store.getState().update((draft) => updateTrackGroup(draft, groupId, { collapsed }));
}

export function renameGroup(store: EditorStore, groupId: string, name: string) {
  store.getState().update((draft) => updateTrackGroup(draft, groupId, { name }));
}

/** Shows or hides all tracks of a group (hidden when any is shown). */
export function toggleGroupHidden(store: EditorStore, groupId: string) {
  const doc = store.getState().doc;
  const tracks = groupTrackNumbers(doc, groupId);
  const hide = tracks.some((track) => !trackInfo(doc, track)?.hidden);
  store.getState().update((draft) => {
    for (const track of tracks) setTrackHidden(draft, track, hide);
  });
}

/** Removes a group together with its tracks and what lies on them. */
export function deleteGroup(store: EditorStore, groupId: string) {
  store.getState().update((draft) => {
    const tracks = groupTrackNumbers(draft, groupId);
    for (const track of [...tracks].reverse()) removeTrack(draft, track);
  });
}
